import { MAIN_QUESTS_GROUP, SIDE_QUESTS_GROUP, appConfig } from "@/constants";
import {
  EarningType,
  QuestTileType,
  type RuleId,
} from "@/domain/gems-game/domain";
import {
  Quest as MainQuest,
  type Platform,
} from "@/domain/gems-game/domain/lib/Quest";
import type { AllDualVariantIconKeys, AllIconKeys } from "@biom3/react";
import { getLinkedDocument } from "@cms/utils/documents";
import type {
  Content,
  ContentRelationshipField,
  FilledContentRelationshipField,
} from "@prismicio/client";
import type {
  OtherQuest,
  OwnershipQuest,
  PlayVideoMimeType,
  Quest,
  QuestGroup,
  QuestKey,
  Quest as QuestType,
} from "@ui-kit/QuestList";
import { notifyError } from "../monitoring";

// rather than setting the key, type, and icon in the prismic document,
// we can derive them based on a single field to save on user input
const mapQuestType = (
  questType: string,
): {
  key: QuestKey;
  type: QuestType["type"];
  icon: AllIconKeys;
} => {
  switch (questType) {
    case "Follow on X":
      return { key: "x-v2", type: "link", icon: "X" };
    case "Follow on Discord":
      return { key: "discord-v2", type: "link", icon: "Discord" };
    case "Post on X":
      return { key: "x-post-tweet-validation", type: "link", icon: "X" };
    case "Like and Retweet on X":
      return { key: "x-like-and-retweet-validation", type: "link", icon: "X" };
    case "Watch a video":
      return { key: "watch", type: "video", icon: "Play" };
    case "Visit a website":
      return { key: "website", type: "link", icon: "ApplicationWeb" };
    case "Gameplay based":
      return { key: "in-game", type: "custom", icon: "PlayGame" };
    case "Own an asset":
      return { key: "ownership", type: "custom", icon: "Coins" };
    case "Bonus reward":
      return { key: "bonus", type: "custom", icon: "ImxRewards" };
    case "Follow the game":
      return { key: "follow", type: "custom", icon: "Add" };
    case "Complete a quiz":
      return { key: "quiz", type: "quiz", icon: "Edit" };
    case "Play the game daily":
      return { key: "daily-game-play", type: "custom", icon: "PlayGame" };
    case "Follow on Telegram":
      return { key: "telegram", type: "link", icon: "Telegram" };
    case "Telegram validation":
      return { key: "telegram-v2", type: "link", icon: "Telegram" };
    case "Social engagement":
      return { key: "social-engagement", type: "custom", icon: "SupportChat" };
    case "Steam wishlist validation":
      return { key: "steam", type: "link", icon: "SteamStore" };
    case "App store validation":
      return {
        key: "app-store-image-validation",
        type: "custom",
        icon: "ImxRewards",
      };

    default:
      throw new Error(`Unknown quest type: ${questType}`);
  }
};

const requiredCast = <T>(
  fieldName: string,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: any,
): T => {
  if (field === null || field === undefined) {
    throw new Error(`${fieldName} is missing but is a required field`);
  }

  return field as T;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const optionalCast = <T>(field: any): T | null => {
  return field === null || field === undefined ? null : (field as T);
};

const constructInitialQuest = (
  key: QuestKey,
  quest: Omit<Quest, "key">,
): Quest => {
  return key === "ownership"
    ? ({ ...quest, key } as OwnershipQuest)
    : ({ ...quest, key } as OtherQuest);
};

const mapToRuleModal = (
  gameUid: string,
  ruleModal: ContentRelationshipField<"onboarding_gameplay_quest">,
) => {
  const linkedRuleModal = getLinkedDocument<
    Content.OnboardingGameplayQuestDocument,
    Content.OnboardingGameplayQuestDocumentData
  >(ruleModal);

  const logo = requiredCast<string>("logo", linkedRuleModal?.data?.logo?.url);
  const background = requiredCast<string>(
    "background",
    linkedRuleModal?.data?.background?.url,
  );
  const gemsAmount = requiredCast<number>(
    "gems_amount",
    linkedRuleModal?.data?.gems_amount,
  );
  const earningType = requiredCast<string>(
    "earning_type",
    linkedRuleModal?.data?.earning_type,
  );
  const rules = requiredCast<{ rule: string }[]>(
    "rules",
    linkedRuleModal?.data?.rules,
  );
  const startDate = requiredCast<string>(
    "start_date",
    linkedRuleModal?.data?.start_date,
  );
  const endDate = requiredCast<string>(
    "end_date",
    linkedRuleModal?.data?.end_date,
  );
  const platforms = requiredCast<{ platform: Platform }[]>(
    "available_on",
    linkedRuleModal?.data?.available_on,
  );
  const caption = requiredCast<string>(
    "caption",
    linkedRuleModal?.data?.caption,
  );

  const rulesModal = {
    id: `${gameUid}-quest` as RuleId,
    caption,
    logo: new URL(logo),
    backgroundMediaUrl: new URL(background),
    gemsAmount: gemsAmount.toString(),
    analyticsId: `${gameUid}-quest`,
    type: QuestTileType.MainQuest,
    earningType:
      earningType === "off chain" ? EarningType.OffChain : EarningType.OnChain,
    rules: rules ? { items: rules.map(({ rule }) => rule) } : { items: [] },
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    availableOn: platforms ? platforms.map(({ platform }) => platform) : [],
  };

  return new MainQuest(rulesModal);
};

const mapToAssetHolding = (
  document: FilledContentRelationshipField<
    Content.OnboardingQuestDocument,
    string,
    Content.OnboardingQuestDocumentData
  >,
  assetHoldingType: "erc20" | "erc721" | "erc1155",
): Quest["assetHoldingToken"] => {
  const assetHoldingToken: Quest["assetHoldingToken"] = {
    name: "",
    image: "",
    collectionAddress: "",
    tokenId: "",
    minimumBalance: 0,
  };

  const assetHoldingName = optionalCast<string>(document?.data?.asset_name);
  if (assetHoldingName) assetHoldingToken.name = assetHoldingName;

  const assetHoldingImage = optionalCast<string>(
    document?.data?.asset_image?.url,
  );
  if (assetHoldingImage) assetHoldingToken.image = assetHoldingImage;

  const assetHoldingCollectionAddress = optionalCast<string>(
    document?.data?.asset_collection_address,
  );
  if (assetHoldingCollectionAddress)
    assetHoldingToken.collectionAddress = assetHoldingCollectionAddress;

  if (assetHoldingType === "erc1155") {
    const assetHoldingTokenId = optionalCast<string>(document?.data?.token_id);
    if (assetHoldingTokenId) assetHoldingToken.tokenId = assetHoldingTokenId;
  }

  const assetHoldingPointsMultiplier = optionalCast<number>(
    document?.data?.points_multiplier,
  );
  if (assetHoldingPointsMultiplier)
    assetHoldingToken.pointsMultiplier = assetHoldingPointsMultiplier;

  const assetHoldingMinimumBalance = optionalCast<number>(
    document?.data?.minimum_balance,
  );
  if (assetHoldingMinimumBalance)
    assetHoldingToken.minimumBalance = assetHoldingMinimumBalance;

  const assetHoldingPurchaseUrl = optionalCast<string>(
    document?.data?.purchase_url,
  );
  if (assetHoldingPurchaseUrl)
    assetHoldingToken.purchaseUrl = assetHoldingPurchaseUrl;

  return assetHoldingToken;
};

const mapToQuest = (
  gameUid: string,
  questDocument: Content.OnboardingQuestGroupDocumentDataQuestsListItem,
): Quest | null => {
  try {
    const { quest } = questDocument;

    if (!quest) return null;

    const linkedDocument = getLinkedDocument<
      Content.OnboardingQuestDocument,
      Content.OnboardingQuestDocumentData
    >(quest);

    if (!linkedDocument) return null;

    // currently only points and gems are supported as reward types
    const isPoints = linkedDocument?.data?.reward_type === "points";

    const questType = optionalCast<string>(linkedDocument?.data?.quest_type);

    if (!questType) return null;

    // we can derive the key, type, and icon from the prismic quest type
    const { key, type, icon } = mapQuestType(questType);

    const increase = requiredCast<number>(
      "increase",
      linkedDocument?.data?.increase,
    );

    let badgeTemplate = "";
    if (increase > 0) {
      badgeTemplate = isPoints
        ? "+<increase> <icon>"
        : "+<increase> <icon> Gems";
    }

    const initialQuest: Omit<Quest, "key"> = {
      type,
      icon,
      label: requiredCast<string>("label", linkedDocument?.data?.label),
      caption: optionalCast<string>(linkedDocument?.data?.caption) ?? "",
      increase,
      badgeIcon: isPoints ? "Sparkle" : "ImxRewards",
      badgeTemplate,
      eventId: requiredCast<string>("eventId", linkedDocument?.data?.event_id),
      ruleId: requiredCast<string>("ruleId", linkedDocument?.data?.rule_id),
      perpetualRewardsEligible:
        linkedDocument?.data?.eligible_for_perpetual_rewards ?? false,
    };

    const mappedQuest = constructInitialQuest(key, initialQuest);

    const linkUrl = optionalCast<string>(linkedDocument?.data?.link_url);
    if (linkUrl) mappedQuest.linkUrl = linkUrl;

    const mimeType = optionalCast<PlayVideoMimeType>(
      linkedDocument?.data?.video_mime_type,
    );
    if (mimeType && type === "video") mappedQuest.videoMimeType = mimeType;

    const videoUrl = optionalCast<string>(linkedDocument?.data?.video_url);
    if (videoUrl) {
      // video/mp4 should be internally hosted with a prefix, whereas embedded YouTube videos will have a complete link
      const prefix =
        mappedQuest.videoMimeType === "video/mp4"
          ? appConfig.PLAY_ASSETS_CDN_BASE_URL
          : "";
      mappedQuest.videoUrl = `${prefix}${videoUrl}`;
    }

    const waitTimeInHrs = optionalCast<number>(
      linkedDocument?.data?.wait_time_in_hrs,
    );
    if (waitTimeInHrs) mappedQuest.waitTimeInHrs = waitTimeInHrs;

    const tweetText = optionalCast<string>(linkedDocument?.data?.tweet_text);
    if (tweetText) mappedQuest.tweetText = tweetText;

    const tweetPlaceholder = optionalCast<string>(
      linkedDocument?.data?.tweet_placeholder,
    );
    if (tweetPlaceholder) mappedQuest.tweetPlaceholder = tweetPlaceholder;

    const tweetUsername = optionalCast<string>(
      linkedDocument?.data?.tweet_username,
    );
    if (tweetUsername) mappedQuest.tweetUsername = tweetUsername;

    if (linkedDocument?.data?.rule_modal) {
      mappedQuest.ruleModal = mapToRuleModal(
        gameUid,
        linkedDocument?.data?.rule_modal,
      );
    }

    const xPostData: Quest["XPostData"] = {};

    if (linkedDocument?.data?.x_account_handle) {
      xPostData.handle = linkedDocument?.data?.x_account_handle;
    }

    if (linkedDocument?.data?.x_account_name) {
      xPostData.name = linkedDocument?.data?.x_account_name;
    }

    if (linkedDocument?.data?.x_account_image?.url) {
      xPostData.image = linkedDocument?.data?.x_account_image?.url;
    }

    if (
      linkedDocument?.data?.x_post_body_list &&
      linkedDocument?.data?.x_post_body_list?.length > 0
    ) {
      const items: string[] = [];
      for (const item of linkedDocument?.data?.x_post_body_list ?? []) {
        if (item.x_post_body_line) {
          items.push(String(item.x_post_body_line));
        }
      }
      if (items.length > 0) {
        xPostData.postBody = items;
      }
    }

    if (linkedDocument?.data?.target_retweet_id) {
      mappedQuest.likeRetweetId = linkedDocument?.data?.target_retweet_id;
    }

    if (linkedDocument?.data?.recurring_refresh_period_days) {
      mappedQuest.recurringRefreshPeriodDays =
        linkedDocument?.data?.recurring_refresh_period_days;
    }

    if (linkedDocument?.data?.start_time) {
      mappedQuest.startTime = new Date(linkedDocument?.data?.start_time);
    }

    if (linkedDocument?.data?.x_posted_time) {
      xPostData.postedTime = new Date(linkedDocument.data.x_posted_time);
    }

    if (linkedDocument?.data?.x_like_count) {
      xPostData.likeCount = linkedDocument?.data?.x_like_count;
    }

    if (linkedDocument?.data?.x_retweet_count) {
      xPostData.retweetCount = linkedDocument?.data?.x_retweet_count;
    }

    if (linkedDocument?.data?.x_comment_count) {
      xPostData.commentCount = linkedDocument?.data?.x_comment_count;
    }

    if (Object.keys(xPostData).length > 0) {
      mappedQuest.XPostData = xPostData;
    }

    if (linkedDocument?.data?.tweet_image_required) {
      mappedQuest.tweetRequiresImage =
        linkedDocument?.data?.tweet_image_required;
    }

    // check for ownership based quest fields
    const assetHoldingType = optionalCast<"erc20" | "erc721" | "erc1155">(
      linkedDocument?.data?.asset_holding_type,
    );

    if (assetHoldingType) {
      mappedQuest.assetHoldingType = assetHoldingType;

      const assetHoldingToken = mapToAssetHolding(
        linkedDocument,
        assetHoldingType,
      );
      mappedQuest.assetHoldingToken = assetHoldingToken;
    }

    const endDate = optionalCast<string>(linkedDocument?.data?.end_date);
    if (endDate) mappedQuest.endDate = new Date(endDate);

    const recurring = optionalCast<boolean>(linkedDocument?.data?.recurring);
    if (recurring) mappedQuest.recurring = recurring;

    const recurringValidFrom = optionalCast<string>(
      linkedDocument?.data?.recurring_valid_from,
    );
    if (recurringValidFrom)
      mappedQuest.recurringValidFrom = new Date(recurringValidFrom);

    if (linkedDocument?.data?.action_validation_type) {
      mappedQuest.actionValidationType =
        linkedDocument?.data?.action_validation_type;
    }

    return mappedQuest;
  } catch (error) {
    console.error(error);
    notifyError(error, "prismicQuestMappingError", { questDocument });
    return null;
  }
};

export const mapToQuestGroup = (
  gameUid: string,
  questGroups: Content.OnboardingQuestGroupDocument<string>[] | null,
): QuestGroup[] | null => {
  try {
    if (!questGroups) return null;

    const mappedQuestGroups: Array<{ order: number; questGroup: QuestGroup }> =
      [];
    for (const questGroup of questGroups) {
      const { data } = questGroup;
      const { exclude_production } = data;

      // there is currently a usecase where we want to be able to omit certain quest groups from production,
      // if partners are testings against the quest API we only want those quests in non-prod envs
      if (appConfig.ENVIRONMENT === "production" && exclude_production)
        continue;

      const mappedQuests: Quest[] = [];
      for (const questDocument of data.quests_list) {
        const mappedQuest = mapToQuest(gameUid, questDocument);

        if (!mappedQuest) continue;

        mappedQuests.push(mappedQuest);
      }

      const group: QuestGroup = {
        key: data.key?.split(" ").join("-") ?? "",
        header: {
          title: requiredCast<string>("headerTitle", data.header_title),
        },
        progressIcon:
          (data.progress_icon as AllDualVariantIconKeys) ?? "ImxRewards",
        totalGemsGranted: requiredCast<number>(
          "totalGemsGranted",
          data.total_gems_granted,
        ),
        hideOnCompletion: data.hide_on_completion,
        lockedUntilPrevious: data.locked_until_previous,
        quests: mappedQuests,
        questGroup: data.gameplay_quests
          ? MAIN_QUESTS_GROUP
          : SIDE_QUESTS_GROUP,
      };

      const headerImage = optionalCast<string>(data.header_image_url);
      if (headerImage && group.header) {
        group.header.image = headerImage;
      }

      const headerDescription = optionalCast<string>(data.header_description);
      if (headerDescription && group.header) {
        group.header.description = headerDescription;
      }

      const progressTemplate = optionalCast<string>(data.progress_template);
      if (progressTemplate && group.progressTemplate) {
        group.progressTemplate = progressTemplate;
      }

      if (data.next_quest) {
        const linkedNextQuest = getLinkedDocument<
          Content.OnboardingNextQuestDocument,
          Content.OnboardingNextQuestDocumentData
        >(data.next_quest);
        const { url, title, label, caption } = linkedNextQuest?.data ?? {};

        if (url || title || label || caption) {
          group.nextQuest = {
            url: url ?? "",
            title: title ?? "",
            label: label ?? "",
            caption: caption ?? "",
          };
        }
      }

      if (data.intro_modal) {
        const linkedIntroModal = getLinkedDocument<
          Content.OnboardingQuestIntroModalDocument,
          Content.OnboardingQuestIntroModalDocumentData
        >(data.intro_modal);
        const { title, description, foreground_image } =
          linkedIntroModal?.data ?? {};

        if (title || description || foreground_image) {
          group.introModal = {
            title: title ?? "",
            description: description ?? "",
            foregroundImage: foreground_image ?? "",
          };
        }
      }

      if (data.exit_modal) {
        const linkedExitModal = getLinkedDocument<
          Content.OnboardingQuestIntroModalDocument,
          Content.OnboardingQuestIntroModalDocumentData
        >(data.exit_modal);
        const { title, description, foreground_image } =
          linkedExitModal?.data ?? {};

        if (title || description || foreground_image) {
          group.exitModal = {
            title: title ?? "",
            description: description ?? "",
            foregroundImage: foreground_image ?? "",
          };
        }
      }

      mappedQuestGroups.push({
        order: data.order ?? mappedQuestGroups.length,
        questGroup: group,
      });
    }

    return mappedQuestGroups
      .sort((a, b) => a.order - b.order)
      .map(({ questGroup }) => questGroup);
  } catch (error) {
    console.error(error);
    notifyError(error, "prismicQuestMappingError", { questGroups });
    return null;
  }
};
